<!--
	@component

	The `oncountchange` listener is available when count in Cart change.

	To ensure valid HTML, you might change the heading level for the title. (It defaults to `3`.)

	The `max` prop allows you to limit the amount by a reasonable boundary.
	Note that it is never possible to select more than 999 pieces.
-->
<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import PreloadedPrice from "./PreloadedPrice.svelte";
	import type { ProductForCard } from "./ProductCard.js";
	import translations from "./ProductCard.translations.json" with { type: "json" };
	import ProductCardCartOptions from "./ProductCardCartOptions.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import ProductCardMediaFallback from "./ProductCardMediaFallback.svelte";
	import ProductTags from "./ProductTags.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Heading from "./common/Heading.svelte";
	import type { HeadingLevel } from "./common/HeadingLevel.js";
	import { getTagsForProductCard } from "./getTagsForProductCard.js";

	const { tenant } = getGlobalTemplateProps();

	let {
		/* eslint-disable prefer-const */
		oncountchange,
		titleHeadingLevel = 3,
		product,
		subcategory,
		/* eslint-enable prefer-const */
	}: {
		oncountchange?(this: void, count: number): void;
		titleHeadingLevel?: HeadingLevel;
		product: ProductForCard;
		subcategory: SubcategoryForCustomer;
	} = $props();

	let selectedVariant = $derived.by(() => {
		const firstVariantOnStock = product.variants.find((variant) => {
			const stock = getProductStockAmount(product.stock, variant, product.attributes);
			return stock > 0;
		});
		if (!firstVariantOnStock) {
			throw new Error("Product has no variants on stock.");
		}
		return firstVariantOnStock.id;
	});

	const productVariant = $derived(getProductOfVariant(product, selectedVariant));
	const primaryMedia = $derived(getProductPrimaryMedia(productVariant, product.attributes));
	const secondaryMedia = $derived(
		product.gallery.find((media) => media.photo && media.photo.path !== primaryMedia?.path)?.photo,
	);
	const link = $derived(
		getProductDetailPath(tenant.locale, product, subcategory, productVariant.variant, product.attributes),
	);
	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, product));

	const max = $derived(getProductStockAmount(productVariant.stock, productVariant.variant, product.attributes));

	const translate = getTranslate(translations);

	const tags = $derived(getTagsForProductCard({ product, locale: tenant.locale, subcategory }));
	const hasTags = $derived(Object.keys(tags).length > 0);
</script>

{#if productVariant}
	<article class="flex flex-col w-81.5 group/product-card">
		<div
			class={[
				"bg-[#F6F6F6] h-80 flex overflow-hidden flex-col p-4 rounded-2xl relative",
				hasTags ? "justify-between" : "justify-end",
			]}
		>
			<ProductTags {tags} class="pointer-events-none" />
			<a href={link} class={["absolute inset-0 size-full bg-inherit"]} aria-hidden="true">
				{#if primaryMedia}
					<ProductCardMedia mediaSrc={primaryMedia} />
					{#if secondaryMedia}
						<ProductCardMedia mediaSrc={secondaryMedia} secondary />
					{/if}
				{:else}
					<ProductCardMediaFallback />
				{/if}
			</a>

			<ProductCardCartOptions
				showToasts
				product={productVariant}
				attributes={product.attributes}
				onvariantidchange={(variantId: string): void => {
					selectedVariant = variantId;
				}}
				{max}
				{oncountchange}
			/>
		</div>
		<Heading
			level={titleHeadingLevel}
			text={link ? `[${link}][[${heading}]]` : heading}
			class={[
				"font-antipol text-fcrdarkgray text-base md:text-1.5xl mt-4 md:mt-6",
				link && "hover:underline group-hover/product-card:underline [&>a]:no-underline",
				link && "hover:text-fcrbrown group-hover/product-card:text-fcrbrown transition-colors",
				link && "[div:has(>a:not([data-fslightbox]):hover)+&]:underline",
				link && "[div:has(>a:not([data-fslightbox]):hover)+&]:text-fcrbrown",
			]}
		/>
		<div class="mt-1 md:mt-2 text-fcrdarkgray text-sm/normal md:text-base">
			{#if max === 0}
				<Formatted text={`${translate("soldOut")} –`} />
			{/if}
			<PreloadedPrice class="h-6">
				{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
					{@const productPrice = getPriceForCustomerOfProductVariant(
						productVariant,
						currency,
						destinationCountry,
						EconomicSubjectVatType.NotVatSubject,
					).withTax}
					{#if currency === Currency.CZK}
						<Formatted text={formatPrice(Math.ceil(productPrice), currency, tenant.locale, false)} />
					{:else}
						<Formatted text={formatPrice(productPrice, currency, tenant.locale, true)} />
					{/if}
				{/snippet}
			</PreloadedPrice>
		</div>
	</article>
{/if}
